// ---------------------------------------------------------------------------
// Story Beats (PLAN.md §42): die Dramaturgie als eigene Ebene. Eine Beat-Liste am Manuskript (Vorlage oder eigene) sagt, was an welcher
// Stelle der Lesereihenfolge geschehen soll; Szenen tragen die Beats. Die Ist-Stelle ergibt sich aus den Wörtern davor, leere Szenen
// zählen mit einer Schätzung – so funktioniert die Planung auch vor dem ersten Wort. Dazu die Wertverschiebung je Szene (Glückskurve),
// die Lage der Beats in der Weltzeit, Prüfungen und eine Synopsis als Markdown.
// ---------------------------------------------------------------------------

import { displayOf, type StoryCtx } from './ctx.js';
import { draftOf, manuscriptOf, narrativeOrder, wordCount, worldOrder, type NarrativeOrder, type SceneEntry } from './narrative.js';
import { plainRefs } from './refs.js';
import { isScene } from './scene.js';
import type { Beat, BeatSheet, StoryNode } from './types.js';

export interface BeatTemplateBeat {
  title: string;
  description: string;
  /** Soll als Anteil (0..1); fehlend = keins */
  target?: number;
  act?: boolean;
}

export interface BeatTemplate {
  key: string;
  name: string;
  /** ein Satz, wofür sie taugt */
  hint: string;
  beats: BeatTemplateBeat[];
}

const b = (title: string, target: number, description: string, act = false): BeatTemplateBeat => ({ title, target, description, ...(act ? { act } : {}) });

/** Die mitgelieferten Vorlagen; beim Anlegen kopiert und danach frei änderbar. Die Beschreibung ist der Schreibauftrag: was geschieht, wozu, und eine Frage zum Prüfen. */
export const BEAT_TEMPLATES: readonly BeatTemplate[] = [
  {
    key: 'drei-akte',
    name: 'Drei Akte',
    hint: 'Das klassische Gerüst: Aufbau, Konfrontation, Auflösung.',
    beats: [
      b('Exposition', 0, 'Zeige die Welt, die Hauptfigur und ihren Alltag – und was ihr fehlt. Der Leser soll sie kennen, bevor sich etwas ändert. Frage: Was steht für sie auf dem Spiel?', true),
      b('Auslösendes Ereignis', 0.12, 'Etwas stört das Gleichgewicht – eine Nachricht, eine Begegnung, ein Verlust – und stellt die Hauptfigur vor eine Frage, der sie nicht ausweichen kann. Frage: Warum gerade jetzt, warum gerade sie?'),
      b('Erster Wendepunkt', 0.25, 'Die Hauptfigur entscheidet sich und betritt die neue Lage; es gibt kein Zurück. Ab hier hat sie ein Ziel. Frage: Was gibt sie für diese Entscheidung auf?', true),
      b('Mittelpunkt', 0.5, 'Eine Enthüllung oder ein Umschwung verändert, worum es geht: Die Hauptfigur hört auf zu reagieren und beginnt zu handeln, der Einsatz steigt. Frage: Was weiß sie jetzt, was sie vorher nicht wusste?'),
      b('Zweiter Wendepunkt', 0.75, 'Der Tiefpunkt – oder eine Wendung, die zum Finale zwingt. Die alte Strategie trägt nicht mehr. Frage: Was muss die Hauptfigur erkennen, um weiterzumachen?', true),
      b('Höhepunkt', 0.9, 'Die entscheidende Konfrontation: Die Hauptfigur stellt sich dem größten Hindernis mit allem, was sie gelernt hat; die zentrale Frage der Geschichte wird beantwortet. Frage: Zu welchem Preis?'),
      b('Auflösung', 0.97, 'Die neue Ordnung danach: Die Folgen des Höhepunkts, lose Fäden werden verknüpft. Zeige, wie sich die Hauptfigur und ihre Welt verändert haben. Frage: Welches Bild bleibt dem Leser?'),
    ],
  },
  {
    key: 'save-the-cat',
    name: 'Save the Cat',
    hint: '15 Beats nach Blake Snyder, mit festen Anteilen am Buch.',
    beats: [
      b('Eröffnungsbild', 0, 'Ein Bild der Welt und der Hauptfigur vor der Veränderung – Ton, Stimmung und Genre in einer Szene; das Gegenstück zum Schlussbild. Frage: Wo steht die Hauptfigur am Anfang?', true),
      b('Thema', 0.05, 'Jemand spricht aus, was die Hauptfigur lernen muss – beiläufig und ohne dass sie es versteht. Das ist die Lektion der ganzen Geschichte. Frage: Welche Wahrheit will sie noch nicht sehen?'),
      b('Aufbau', 0.06, 'Alltag, Schwächen und Menschen der Hauptfigur; was in ihrem Leben fehlt. Stelle die wichtigen Figuren vor und deute an, was sich ändern muss. Frage: Was geschähe, wenn alles so bliebe?'),
      b('Auslöser', 0.1, 'Das Ereignis, das alles in Gang setzt – eine Nachricht, eine Begegnung, ein Unglück. Danach kann ihr Leben nicht so weitergehen. Frage: Was zwingt sie aus ihrer Komfortzone?'),
      b('Zögern', 0.12, 'Die Hauptfigur zweifelt, wägt ab, sträubt sich – und stellt die Frage, die die Geschichte beantworten wird. Frage: Was hält sie zurück, was spricht dafür, es zu wagen?'),
      b('Schritt in den zweiten Akt', 0.2, 'Sie entscheidet sich selbst und betritt die neue Welt; die alte bleibt zurück. Frage: Welches Ziel verfolgt sie ab jetzt?', true),
      b('B-Geschichte', 0.22, 'Eine Nebenhandlung – oft eine Beziehung zu einer neuen Figur –, die das Thema trägt; hier holt sich die Hauptfigur Kraft für ihre Lektion. Frage: Wer spiegelt ihr, was ihr fehlt?'),
      b('Spaß und Spiele', 0.3, 'Das Versprechen der Prämisse: Die Hauptfigur erkundet die neue Welt, Erfolge und Rückschläge wechseln sich ab – die Szenen, für die der Leser das Buch aufschlägt. Frage: Was macht die neue Welt reizvoll?'),
      b('Mittelpunkt', 0.5, 'Ein falscher Sieg oder eine falsche Niederlage; der Einsatz steigt, die Uhr beginnt zu ticken. Oft treffen hier A- und B-Geschichte zusammen. Frage: Warum ist es schlimmer, als es aussieht?'),
      b('Die Gegner rücken näher', 0.55, 'Druck von außen, Risse im Inneren: Die Gegner sammeln sich, das Team zerfällt, alte Schwächen holen die Hauptfigur ein. Frage: Was läuft schief, weil sie sich noch nicht geändert hat?'),
      b('Alles ist verloren', 0.75, 'Der Tiefpunkt; oft stirbt etwas oder jemand – ein Mentor, eine Hoffnung, eine Beziehung. Die Hauptfigur ist weiter vom Ziel entfernt als je zuvor. Frage: Was verliert sie?'),
      b('Dunkle Nacht der Seele', 0.77, 'Verzweiflung – und die Einsicht, die bisher fehlte: Die Hauptfigur trauert, blickt zurück und begreift endlich das Thema. Frage: Was erkennt sie über sich selbst?'),
      b('Schritt in den dritten Akt', 0.8, 'Mit der Einsicht findet die Hauptfigur den Weg; A- und B-Geschichte verbinden sich zu einer Lösung. Frage: Welchen neuen Plan fasst sie?', true),
      b('Finale', 0.85, 'Sie setzt um, was sie gelernt hat, und löst das Problem – oft in mehreren Schritten, mit einer letzten Überraschung. Frage: Wie beweist sie, dass sie sich verändert hat?'),
      b('Schlussbild', 0.99, 'Das Gegenbild zum Eröffnungsbild: die Hauptfigur in ihrer neuen Welt. Frage: Woran sieht der Leser, was sich verändert hat?'),
    ],
  },
  {
    key: 'heldenreise',
    name: 'Heldenreise',
    hint: 'Zwölf Stufen nach Christopher Vogler.',
    beats: [
      b('Gewohnte Welt', 0, 'Die Heldin in ihrem Alltag, bevor das Abenteuer beginnt: ihre Welt, ihre Wunde, ihr Mangel – damit der Kontrast später sichtbar wird. Frage: Was fehlt ihr, ohne dass sie es weiß?', true),
      b('Ruf des Abenteuers', 0.08, 'Ein Problem oder eine Herausforderung taucht auf und lädt sie ein, die gewohnte Welt zu verlassen. Frage: Was steht auf dem Spiel, wenn sie nicht geht?'),
      b('Weigerung', 0.12, 'Angst und Zweifel halten sie zurück; sie weigert sich oder zögert – das zeigt, wie groß das Wagnis ist. Frage: Wovor hat sie Angst?'),
      b('Begegnung mit dem Mentor', 0.17, 'Rat, Ausrüstung oder Mut von jemandem, der den Weg kennt. Der Mentor bereitet sie vor, gehen muss sie selbst. Frage: Was gibt er ihr mit?'),
      b('Überschreiten der ersten Schwelle', 0.25, 'Sie verlässt die gewohnte Welt und lässt sich auf das Abenteuer ein; ab hier gelten neue Regeln. Frage: Welcher Schritt ist nicht mehr umkehrbar?', true),
      b('Bewährungsproben, Verbündete, Feinde', 0.32, 'Sie lernt die Regeln der neuen Welt, findet Verbündete und erkennt ihre Feinde; jede Probe macht sie stärker oder zeigt ihre Grenzen. Frage: Wem kann sie trauen?'),
      b('Vordringen zur tiefsten Höhle', 0.42, 'Die Vorbereitung auf die größte Gefahr: Sie nähert sich dem Ort oder der Begegnung, die sie am meisten fürchtet; Spannung und Zweifel wachsen. Frage: Was könnte schiefgehen?'),
      b('Entscheidende Prüfung', 0.5, 'Die Begegnung mit dem Tod oder der größten Angst. Sie muss alles riskieren; ein Teil von ihr stirbt. Frage: Was muss sie loslassen, um zu bestehen?'),
      b('Belohnung', 0.6, 'Sie gewinnt, wonach sie gesucht hat – einen Gegenstand, ein Wissen, eine Versöhnung. Ein Moment des Aufatmens, doch die Gefahr ist nicht vorbei. Frage: Was kostet der Gewinn?'),
      b('Rückweg', 0.75, 'Die Folgen holen sie ein; der Weg zurück ist gefährlich, die Gegner schlagen zurück. Sie muss sich entscheiden, das Abenteuer zu Ende zu bringen. Frage: Was treibt sie heimwärts?', true),
      b('Auferstehung', 0.88, 'Die letzte, höchste Prüfung – sie geht verwandelt daraus hervor; hier zeigt sich, was sie auf der Reise gelernt hat. Frage: Wer ist sie am Ende geworden?'),
      b('Rückkehr mit dem Elixier', 0.97, 'Sie bringt etwas zurück, das ihre Welt verändert – Wissen, Heilung, Freiheit. Der Kreis schließt sich. Frage: Was hat ihre Reise den anderen gebracht?'),
    ],
  },
  {
    key: 'seven-point',
    name: 'Seven-Point',
    hint: 'Sieben Punkte nach Dan Wells – vom Ende her geplant.',
    beats: [
      b('Ausgangslage', 0, 'Die Hauptfigur im Gegenteil des Zustands am Ende: schwach, wo sie stark wird, verloren, wo sie ankommt. Plane diesen Punkt vom Ende her. Frage: Wo beginnt ihr Bogen?', true),
      b('Erste Wende', 0.15, 'Ein Ereignis bringt sie auf den Weg; die Geschichte nimmt Fahrt auf, die Hauptfigur bekommt ein Ziel und eine neue Lage. Frage: Was setzt sie in Bewegung?', true),
      b('Erster Druckpunkt', 0.35, 'Der Gegner zeigt seine Macht; die Hauptfigur muss handeln und lernt dabei oft, was sie für die Mitte braucht. Frage: Welcher Druck zwingt sie, sich vorzubereiten?'),
      b('Mitte', 0.5, 'Sie wechselt vom Reagieren ins Handeln: Sie fasst einen Entschluss und nimmt ihr Schicksal in die Hand. Frage: Was bringt sie dazu, selbst aktiv zu werden?'),
      b('Zweiter Druckpunkt', 0.62, 'Noch mehr Druck – alles scheint verloren: Pläne scheitern, Verbündete fallen weg, die Hauptfigur steht allein. Frage: Was nimmt man ihr?'),
      b('Zweite Wende', 0.8, 'Sie findet, was sie zum Sieg braucht – die letzte Erkenntnis, das fehlende Stück, die eigene Stärke. Frage: Was hatte sie die ganze Zeit, ohne es zu wissen?', true),
      b('Auflösung', 0.97, 'Der Höhepunkt und das Ende: das Gegenteil der Ausgangslage; alles, was die Geschichte aufgebaut hat, zahlt sich aus. Frage: Woran zeigt sich ihre Verwandlung?'),
    ],
  },
  {
    key: 'freytag',
    name: 'Freytag',
    hint: 'Die Pyramide des Dramas in fünf Akten.',
    beats: [
      b('Exposition', 0, 'Ort, Zeit, Figuren und der Konflikt im Keim: Die Ausgangslage wird klar, die Gegenkräfte sind schon angelegt. Frage: Welcher Konflikt schlummert unter der Oberfläche?', true),
      b('Steigende Handlung', 0.15, 'Mit dem erregenden Moment spitzt sich der Konflikt zu; Spiel und Gegenspiel verschärfen sich Schritt für Schritt. Frage: Welche Taten treiben den Konflikt voran?', true),
      b('Höhepunkt', 0.45, 'Die Peripetie: Das Geschehen wendet sich, das Glück des Helden kippt; ab hier läuft alles auf das Ende zu. Frage: Welche Entscheidung oder Tat bringt die Wende?', true),
      b('Fallende Handlung', 0.6, 'Das retardierende Moment zögert das Ende hinaus und lässt noch einmal hoffen, während sich die Folgen der Wende entfalten. Frage: Was weckt noch einmal Hoffnung – oder Furcht?', true),
      b('Katastrophe', 0.85, 'Die Lösung des Konflikts – tragisch oder glücklich: Der Held geht unter oder triumphiert, die Ordnung stellt sich neu her. Frage: Warum musste es so enden?', true),
    ],
  },
  { key: 'leer', name: 'Leer', hint: 'Eine eigene Liste ohne Vorgaben.', beats: [] },
];

export const beatTemplate = (key: string | undefined): BeatTemplate | undefined => BEAT_TEMPLATES.find((t) => t.key === key);

/** Eine neue Beat-Liste aus einer Vorlage (ohne Vorlage: leer) */
export function sheetFromTemplate(key: string, newId: () => string, title?: string): BeatSheet {
  const t = beatTemplate(key);
  return sheetFromBeats(title ?? (t && t.key !== 'leer' ? t.name : 'Eigene Struktur'), t?.beats ?? [], newId, t && t.key !== 'leer' ? t.key : undefined);
}

/** Eine neue Beat-Liste aus den Beats einer Vorlage – mitgeliefert (`template`: ihr Schlüssel) oder eigene (ohne) */
export function sheetFromBeats(title: string, beats: readonly BeatTemplateBeat[], newId: () => string, template?: string): BeatSheet {
  return {
    id: newId(),
    title,
    ...(template ? { template } : {}),
    beats: beats.map((x) => ({ id: newId(), title: x.title, ...(x.description ? { description: x.description } : {}), ...(x.target !== undefined ? { target: x.target } : {}), ...(x.act ? { act: true } : {}), sceneIds: [] })),
  };
}

/** Die Beats einer Liste als Vorlage (ohne Szenen), etwa um sie als eigene Vorlage für weitere Geschichten zu speichern */
export const beatsOfSheet = (sheet: BeatSheet): BeatTemplateBeat[] =>
  sheet.beats.map((x) => ({ title: x.title, description: x.description ?? '', ...(x.target !== undefined ? { target: x.target } : {}), ...(x.act ? { act: true } : {}) }));

/** Die Beat-Listen der Geschichte (am Manuskript) */
export const beatSheetsOf = (ctx: StoryCtx): BeatSheet[] => manuscriptOf(ctx)?.beatSheets ?? [];

// ---------------------------------------------------------------------------
// Ist-Stellen in der Lesereihenfolge
// ---------------------------------------------------------------------------

export interface ReadingSpan {
  /** Anteil der Lesereihenfolge vor bzw. nach der Szene (0..1) */
  start: number;
  end: number;
  /** Wörter des Szenentexts */
  words: number;
  /** leer: zählt mit einer Schätzung */
  estimated: boolean;
}

/** Umfang des Buchs in Wörtern: geschätzt aus den Szenen (leere mit Schätzung), das Ziel des Manuskripts und was davon gilt */
export interface ReadingLength {
  estimated: number;
  /** Ziel-Wortzahl des Manuskripts; null ohne */
  target: number | null;
  /** das Größere von beidem: ist das Buch noch nicht ganz geplant, liegen die Szenen im vorderen Teil */
  length: number;
}

const spanCache = new WeakMap<StoryCtx, { spans: Map<string, ReadingSpan>; length: ReadingLength }>();

/** Ziel-Wortzahl des Manuskripts (Gesamtziel); null ohne */
export function manuscriptTarget(ctx: StoryCtx): number | null {
  const t = manuscriptOf(ctx)?.fields.targetWords;
  return typeof t === 'number' && t > 0 ? t : null;
}

function measure(ctx: StoryCtx, order: NarrativeOrder): { spans: Map<string, ReadingSpan>; length: ReadingLength } {
  const cached = spanCache.get(ctx);
  if (cached) return cached;
  const words = new Map(order.scenes.map((s) => [s.scene.id, wordCount(draftOf(s.scene))]));
  const fromTarget = new Map<string, number>();
  for (const ch of order.chapters) {
    const target = ch.chapter.fields.targetWords;
    if (typeof target === 'number' && target > 0 && ch.scenes.length > 0) for (const s of ch.scenes) fromTarget.set(s.scene.id, target / ch.scenes.length);
  }
  const goal = manuscriptTarget(ctx);
  const written = [...words.values()].filter((w) => w > 0);
  const targets = order.scenes.filter((s) => words.get(s.scene.id) === 0 && fromTarget.has(s.scene.id)).map((s) => fromTarget.get(s.scene.id)!);
  const mean = (xs: number[]): number => xs.reduce((a, x) => a + x, 0) / xs.length;
  const fallback = written.length > 0 ? mean(written) : targets.length > 0 ? mean(targets) : goal && order.scenes.length > 0 ? goal / order.scenes.length : 1;
  const weights = order.scenes.map((s) => {
    const w = words.get(s.scene.id)!;
    return w > 0 ? w : (fromTarget.get(s.scene.id) ?? fallback);
  });
  const estimated = weights.reduce((a, w) => a + w, 0);
  const total = Math.max(estimated, goal ?? 0);
  const spans = new Map<string, ReadingSpan>();
  let acc = 0;
  order.scenes.forEach((s, i) => {
    const w = weights[i]!;
    spans.set(s.scene.id, { start: total > 0 ? acc / total : 0, end: total > 0 ? (acc + w) / total : 0, words: words.get(s.scene.id)!, estimated: words.get(s.scene.id) === 0 });
    acc += w;
  });
  const out = { spans, length: { estimated, target: goal, length: total } };
  spanCache.set(ctx, out);
  return out;
}

/**
 * Stelle jeder eingeordneten Szene in der Lesereihenfolge nach Wörtern. Eine leere Szene zählt mit der Ziel-Wortzahl ihres Kapitels
 * geteilt durch dessen Szenen, sonst mit dem Mittel der geschriebenen (bzw. der geschätzten), sonst mit dem Gesamtziel geteilt durch
 * die Szenen, ohne alles mit 1. Ist das Gesamtziel größer als die Schätzung, ist es das Ganze (das Buch ist noch nicht ganz geplant).
 */
export const readingSpans = (ctx: StoryCtx, order: NarrativeOrder = narrativeOrder(ctx)): Map<string, ReadingSpan> => measure(ctx, order).spans;

/** Umfang des Buchs in Wörtern, mit dem die Anteile der Lesereihenfolge gerechnet sind */
export const readingLength = (ctx: StoryCtx, order: NarrativeOrder = narrativeOrder(ctx)): ReadingLength => measure(ctx, order).length;

export interface PlacedBeat {
  beat: Beat;
  /** Stelle in der Liste (ab 0) */
  index: number;
  /** Nummer des Akts, in dem er liegt (ab 1; 0 vor dem ersten Beat mit Aktbeginn) */
  act: number;
  /** tragende Szenen in Lesereihenfolge */
  scenes: SceneEntry[];
  /** zugeordnete Szenen ohne Kapitel (ohne Stelle in der Lesereihenfolge) */
  unplaced: StoryNode[];
  /** Ist-Stelle: Beginn der ersten Szene; null = offen bzw. nur nicht eingeordnete Szenen */
  at: number | null;
  /** Ist − Soll; null ohne beides */
  drift: number | null;
}

/** Die Beats einer Liste mit ihren Szenen und Stellen */
export function placedBeats(ctx: StoryCtx, sheet: BeatSheet, order: NarrativeOrder = narrativeOrder(ctx)): PlacedBeat[] {
  const spans = readingSpans(ctx, order);
  const entries = new Map(order.scenes.map((s) => [s.scene.id, s]));
  let act = 0;
  return sheet.beats.map((beat, index) => {
    if (beat.act) act += 1;
    const scenes = beat.sceneIds.flatMap((id) => (entries.has(id) ? [entries.get(id)!] : [])).sort((x, y) => x.index - y.index);
    const unplaced = beat.sceneIds.flatMap((id) => {
      const n = ctx.nodes.get(id);
      return n && isScene(n) && !entries.has(id) ? [n] : [];
    });
    const at = scenes[0] ? spans.get(scenes[0].scene.id)!.start : null;
    return { beat, index, act, scenes, unplaced, at, drift: at !== null && beat.target !== undefined ? at - beat.target : null };
  });
}

/** Die Hauptliste: die erste Beat-Liste. Sie erscheint in Erzähllinie, Binder und Kompass; null ohne Liste. */
export const mainBeatSheet = (ctx: StoryCtx): BeatSheet | null => beatSheetsOf(ctx)[0] ?? null;

export interface BeatSlot {
  beat: Beat;
  index: number;
  act: number;
  /** Stelle in der Lesereihenfolge: vor der Szene mit diesem Index (`order.scenes`); so viele wie Szenen = hinter der letzten */
  slot: number;
}

/**
 * Wo die offenen Beats (ohne Szene) hingehören: an die Grenze zwischen zwei Szenen, die ihrem Soll am nächsten liegt – aber hinter der
 * ersten Szene des Beats davor und vor der des Beats danach (soweit sie Szenen haben); ohne Soll gleich hinter dem Beat davor.
 */
export function openBeatSlots(ctx: StoryCtx, sheet: BeatSheet, order: NarrativeOrder = narrativeOrder(ctx)): BeatSlot[] {
  const spans = readingSpans(ctx, order);
  const n = order.scenes.length;
  const last = order.scenes[n - 1];
  const bounds = [...order.scenes.map((s) => spans.get(s.scene.id)!.start), last ? spans.get(last.scene.id)!.end : 0];
  const placed = placedBeats(ctx, sheet, order);
  const first = (p: PlacedBeat): number | null => p.scenes[0]?.index ?? null;
  return placed.flatMap((p, i) => {
    if (p.beat.sceneIds.length > 0) return [];
    const before = placed.slice(0, i).flatMap((q) => (first(q) === null ? [] : [first(q)! + 1]));
    const after = placed.slice(i + 1).flatMap((q) => (first(q) === null ? [] : [first(q)!]));
    const lo = Math.max(0, ...before);
    const hi = Math.max(lo, Math.min(n, ...after));
    let slot = lo;
    if (p.beat.target !== undefined) {
      const t = p.beat.target;
      slot = bounds.reduce((best, b, k) => (Math.abs(b - t) < Math.abs(bounds[best]! - t) ? k : best), 0);
    }
    return [{ beat: p.beat, index: p.index, act: p.act, slot: Math.min(hi, Math.max(lo, slot)) }];
  });
}

export interface SlotPlace {
  /** Kapitel, in das eine Szene an dieser Stelle käme, und die Szene, vor der sie stünde (null: ans Ende) */
  chapterId: string | null;
  beforeId: string | null;
  /** die Stelle liegt zwischen zwei Kapiteln (vor der ersten Szene eines Kapitels oder hinter der letzten): dort passt ein eigenes Kapitel */
  boundary: boolean;
  /** für ein neues Kapitel an dieser Stelle: wo es steht (Manuskript oder Abschnitt; null ohne Manuskript) und vor welchem Kapitel (null: ans Ende) */
  within: string | null;
  beforeChapterId: string | null;
}

/** Wo etwas an der Stelle `slot` eingefügt wird: eine Szene (hinter der letzten: am Ende des letzten Kapitels) bzw. ein Kapitel */
export function slotPlace(order: NarrativeOrder, slot: number): SlotPlace {
  const at = order.scenes[slot];
  if (at) return { chapterId: at.chapter.id, beforeId: at.scene.id, boundary: at.sceneNumber === 1, within: at.chapter.parentId, beforeChapterId: at.chapter.id };
  const last = order.chapters[order.chapters.length - 1]?.chapter ?? null;
  return { chapterId: last?.id ?? null, beforeId: null, boundary: true, within: last?.parentId ?? order.manuscript?.id ?? null, beforeChapterId: null };
}

/**
 * Anteil des Buchs, der zum Beat gehört: von seinem Soll bis zum Soll des nächsten Beats der Liste mit größerem Soll (beim letzten bis zum
 * Ende). Ein Kapitel für den Beat bekommt so seine Ziel-Wortzahl – dann liegt das Ist leerer Kapitel gleich beim Soll. null ohne Soll.
 */
export function beatShare(sheet: BeatSheet, beatId: string): number | null {
  const i = sheet.beats.findIndex((b) => b.id === beatId);
  const t = sheet.beats[i]?.target;
  if (t === undefined) return null;
  const next = sheet.beats.slice(i + 1).find((b) => b.target !== undefined && b.target > t)?.target ?? 1;
  return Math.max(0, next - t);
}

/** Gerüst je Akt: die offenen Beats nach Akten (in der Reihenfolge der Liste) – je Akt ein Abschnitt („Akt II“; vor dem ersten Akt keiner) */
export interface ScaffoldAct {
  act: number;
  /** Name des Abschnitts; null vor dem ersten Akt */
  title: string | null;
  slots: BeatSlot[];
}

export function scaffoldActs(ctx: StoryCtx, sheet: BeatSheet, order: NarrativeOrder = narrativeOrder(ctx)): ScaffoldAct[] {
  const acts: ScaffoldAct[] = [];
  for (const s of openBeatSlots(ctx, sheet, order)) {
    const last = acts[acts.length - 1];
    if (last && last.act === s.act) last.slots.push(s);
    else acts.push({ act: s.act, title: s.act > 0 ? actName(s.act) : null, slots: [s] });
  }
  return acts;
}

export interface BeatCompass {
  /** Anteil der Lesereihenfolge am Beginn der Szene */
  at: number;
  /** Akt an dieser Stelle (0: vor dem ersten) */
  act: number;
  /** die Beats der Hauptliste, die die Szene trägt */
  carried: PlacedBeat[];
  /** der nächste Beat dahinter: an seiner ersten Szene (`scene`) bzw. (offen) an seinem Soll */
  next: { beat: Beat; at: number; open: boolean; scene: SceneEntry | null } | null;
  /** offene Beats, deren Stelle schon vor dieser Szene liegt */
  overdue: Beat[];
  /** Umfang des Buchs in Wörtern (Anteile × Umfang = Wörter) */
  length: number;
}

/** Wo eine Szene in der Hauptliste steht – für den Kompass über dem Szenentext; null, wenn sie nicht eingeordnet ist */
export function beatCompass(ctx: StoryCtx, sheet: BeatSheet, sceneId: string, order: NarrativeOrder = narrativeOrder(ctx)): BeatCompass | null {
  const span = readingSpans(ctx, order).get(sceneId);
  const entry = order.scenes.find((s) => s.scene.id === sceneId);
  if (!span || !entry) return null;
  const placed = placedBeats(ctx, sheet, order);
  const pos = (p: PlacedBeat): number | null => p.at ?? (p.beat.sceneIds.length === 0 ? (p.beat.target ?? null) : null);
  let act = 0;
  for (const p of placed) if (p.beat.act && pos(p) !== null && pos(p)! <= span.start + 1e-9) act = Math.max(act, p.act);
  const carried = placed.filter((p) => p.beat.sceneIds.includes(sceneId));
  const overdue = openBeatSlots(ctx, sheet, order)
    .filter((s) => s.slot <= entry.index)
    .map((s) => s.beat);
  const ahead = placed.filter((p) => !carried.includes(p) && !overdue.includes(p.beat) && pos(p) !== null && pos(p)! > span.start + 1e-9).sort((x, y) => pos(x)! - pos(y)! || x.index - y.index);
  const next = ahead[0] ? { beat: ahead[0].beat, at: pos(ahead[0])!, open: ahead[0].at === null, scene: ahead[0].scenes[0] ?? null } : null;
  return { at: span.start, act, carried, next, overdue, length: readingLength(ctx, order).length };
}

/** Die Beats, die eine Szene trägt (über alle Listen) */
export function beatsOfScene(ctx: StoryCtx, sceneId: string): { sheet: BeatSheet; beat: Beat }[] {
  return beatSheetsOf(ctx).flatMap((sheet) => sheet.beats.filter((x) => x.sceneIds.includes(sceneId)).map((beat) => ({ sheet, beat })));
}

/**
 * Die Beats in der Weltzeit: die eingeordneten Szenen in der Reihenfolge der Weltzeit (`worldOrder`), je Beat die Stelle seiner ersten
 * Szene darin als Anteil (0..1) und sein Rang unter den Beats mit Szene. So sieht man, wo die Reihenfolge der Beats in der Welt eine
 * andere ist als im Buch.
 */
export function beatWorldPlaces(ctx: StoryCtx, sheet: BeatSheet, order: NarrativeOrder = narrativeOrder(ctx)): { scenes: string[]; beats: Map<string, { at: number; rank: number }> } {
  const world = worldOrder(
    ctx,
    order.scenes.map((s) => s.scene),
  ).map((s) => s.id);
  const pos = new Map(world.map((id, i) => [id, i]));
  const placed = placedBeats(ctx, sheet, order).filter((p) => p.scenes.length > 0);
  const at = (p: PlacedBeat): number => Math.min(...p.scenes.map((s) => pos.get(s.scene.id)!));
  const ranked = [...placed].sort((x, y) => at(x) - at(y) || x.index - y.index);
  const span = Math.max(1, world.length - 1);
  return { scenes: world, beats: new Map(ranked.map((p, rank) => [p.beat.id, { at: at(p) / span, rank }])) };
}

/** Glückskurve: die aufsummierte Wertverschiebung über die Lesereihenfolge, je Szene in ihrer Mitte; null, wenn keine Szene eine hat */
export function fortuneCurve(ctx: StoryCtx, order: NarrativeOrder = narrativeOrder(ctx)): { sceneId: string | null; x: number; value: number }[] | null {
  if (!order.scenes.some((s) => typeof s.scene.shift === 'number')) return null;
  const spans = readingSpans(ctx, order);
  let value = 0;
  return [
    { sceneId: null, x: 0, value: 0 },
    ...order.scenes.map((s) => {
      value += s.scene.shift ?? 0;
      const span = spans.get(s.scene.id)!;
      return { sceneId: s.scene.id, x: span.end, value };
    }),
  ];
}

// ---------------------------------------------------------------------------
// Prüfen und Aufräumen
// ---------------------------------------------------------------------------

/** Ab dieser Abweichung (Anteil der Lesereihenfolge) liegt ein Beat weit vom Soll */
export const BEAT_DRIFT = 0.15;
/** Unter so vielen eingeordneten Szenen sagt die Abweichung wenig (die Stellen springen in großen Schritten) */
const DRIFT_MIN_SCENES = 5;

/** Fehler in Beat-Listen (Server): doppelte IDs, Soll außerhalb 0..1, Szenen, die keine sind, eine unbekannte Figur; sonst null */
export function checkBeatSheets(ctx: StoryCtx, sheets: readonly BeatSheet[]): string | null {
  const ids = new Set<string>();
  for (const sheet of sheets) {
    if (ids.has(sheet.id)) return 'Eine Beat-Liste kommt doppelt vor.';
    ids.add(sheet.id);
    if (!sheet.title.trim()) return 'Eine Beat-Liste braucht einen Titel.';
    if (sheet.entityId && !ctx.entities.has(sheet.entityId)) return `Die Figur der Beat-Liste „${sheet.title}“ gibt es nicht.`;
    for (const beat of sheet.beats) {
      if (ids.has(beat.id)) return `Der Beat „${beat.title}“ kommt doppelt vor.`;
      ids.add(beat.id);
      if (!beat.title.trim()) return `Ein Beat in „${sheet.title}“ braucht einen Titel.`;
      if (beat.target !== undefined && !(Number.isFinite(beat.target) && beat.target >= 0 && beat.target <= 1)) return `Das Soll von „${beat.title}“ muss zwischen 0 und 100 % liegen.`;
      if (new Set(beat.sceneIds).size !== beat.sceneIds.length) return `„${beat.title}“ nennt eine Szene doppelt.`;
      for (const id of beat.sceneIds) {
        const n = ctx.nodes.get(id);
        if (!n || !isScene(n)) return `„${beat.title}“ verweist auf eine Szene, die es nicht gibt.`;
      }
    }
  }
  return null;
}

/** Ohne Verweise auf gelöschte Szenen und Figuren (vor dem Speichern einer geänderten Liste) */
export function tidyBeatSheets(ctx: StoryCtx, sheets: readonly BeatSheet[]): BeatSheet[] {
  return sheets.map((sheet) => ({
    ...sheet,
    ...(sheet.entityId && !ctx.entities.has(sheet.entityId) ? { entityId: null } : {}),
    beats: sheet.beats.map((beat) => ({ ...beat, sceneIds: beat.sceneIds.filter((id) => isScene(ctx.nodes.get(id))) })),
  }));
}

export type BeatFinding =
  /** steht in der Lesereihenfolge vor einem Beat, der in der Liste vor ihm kommt */
  | { kind: 'order'; sheet: BeatSheet; beat: Beat; sceneId: string; other: Beat }
  /** liegt weit vom Soll */
  | { kind: 'drift'; sheet: BeatSheet; beat: Beat; sceneId: string; at: number; target: number }
  /** hat keine Szene, ein späterer Beat schon */
  | { kind: 'open'; sheet: BeatSheet; beat: Beat; later: Beat };

const findingCache = new WeakMap<StoryCtx, BeatFinding[]>();

/** Was an den Beat-Listen auffällt (Logik-Lektor, Bereich Struktur) */
export function beatFindings(ctx: StoryCtx): BeatFinding[] {
  const cached = findingCache.get(ctx);
  if (cached) return cached;
  const order = narrativeOrder(ctx);
  const out: BeatFinding[] = [];
  for (const sheet of beatSheetsOf(ctx)) {
    const placed = placedBeats(ctx, sheet, order);
    let latest: PlacedBeat | null = null;
    for (const p of placed) {
      if (p.at === null) continue;
      if (latest && p.at < latest.at!) out.push({ kind: 'order', sheet, beat: p.beat, sceneId: p.scenes[0]!.scene.id, other: latest.beat });
      else latest = p;
    }
    if (order.scenes.length >= DRIFT_MIN_SCENES) {
      for (const p of placed) {
        if (p.drift !== null && Math.abs(p.drift) > BEAT_DRIFT) out.push({ kind: 'drift', sheet, beat: p.beat, sceneId: p.scenes[0]!.scene.id, at: p.at!, target: p.beat.target! });
      }
    }
    placed.forEach((p, i) => {
      if (p.beat.sceneIds.length > 0) return;
      const later = placed.slice(i + 1).find((q) => q.at !== null);
      if (later) out.push({ kind: 'open', sheet, beat: p.beat, later: later.beat });
    });
  }
  findingCache.set(ctx, out);
  return out;
}

// ---------------------------------------------------------------------------
// Synopsis als Markdown
// ---------------------------------------------------------------------------

/** Anteil als ganze Prozent */
export const percent = (x: number): string => `${Math.round(x * 100)} %`;

const ROMAN = ['', 'I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X'];
export const actName = (n: number): string => `Akt ${ROMAN[n] ?? String(n)}`;

/**
 * Die Beat-Listen als Synopsis: je Liste die Beats in ihrer Reihenfolge mit Akt, Soll und Ist, ihrer Beschreibung und den tragenden
 * Szenen mit Kurzbeschreibung, Ziel, Konflikt und Ausgang. Verweise werden zu ihrem Wortlaut.
 */
export function beatSheetMarkdown(ctx: StoryCtx, sheets: readonly BeatSheet[] = beatSheetsOf(ctx)): string {
  const order = narrativeOrder(ctx);
  const manuscript = manuscriptOf(ctx);
  const title = manuscript ? displayOf(ctx, manuscript).title : displayOf(ctx, ctx.nodes.get(ctx.story.rootId)!).title;
  const text = (v: unknown): string => (typeof v === 'string' ? plainRefs(v).replace(/\s+/g, ' ').trim() : '');
  const lines: string[] = [`# ${title} – Struktur`, ''];
  for (const sheet of sheets) {
    const template = beatTemplate(sheet.template);
    const who = sheet.entityId && ctx.entities.has(sheet.entityId) ? ctx.entities.get(sheet.entityId)! : null;
    const whoName = who ? text(who.fields[ctx.types.get(who.typeId)?.titleKey ?? 'name']) : '';
    lines.push(`## ${sheet.title}${template && template.name !== sheet.title ? ` (${template.name})` : ''}${whoName ? ` – Bogen von ${whoName}` : ''}`, '');
    let act = 0;
    for (const p of placedBeats(ctx, sheet, order)) {
      if (p.beat.act) {
        act = p.act;
        lines.push(`**${actName(act)}**`, '');
      }
      const where = [p.beat.target !== undefined ? `Soll ${percent(p.beat.target)}` : null, p.at !== null ? `Ist ${percent(p.at)}` : null].filter(Boolean).join(' · ');
      lines.push(`### ${p.index + 1}. ${p.beat.title}${where ? ` · ${where}` : ''}`, '');
      if (p.beat.description?.trim()) lines.push(`*${text(p.beat.description)}*`, '');
      const scenes: { label: string; node: StoryNode }[] = [...p.scenes.map((s) => ({ label: s.label, node: s.scene })), ...p.unplaced.map((node) => ({ label: '', node }))];
      if (scenes.length === 0) lines.push('(noch offen)', '');
      else {
        for (const s of scenes) {
          const f = s.node.fields;
          const parts = [text(f.description), ...(['goal', 'conflict', 'outcome'] as const).map((k, i) => (text(f[k]) ? `${['Ziel', 'Konflikt', 'Ausgang'][i]}: ${text(f[k])}` : ''))].filter(Boolean);
          lines.push(`- **${s.label ? `${s.label} ` : ''}${displayOf(ctx, s.node).title}**${parts.length > 0 ? ` – ${parts.join(' ')}` : ''}`);
        }
        lines.push('');
      }
    }
  }
  return `${lines.join('\n').trimEnd()}\n`;
}
